import { AuthenticationService } from "../../core/authenticationService/AuthenticationService";
import { PushNotificationsService } from "../../core/pushNotificationsService/PushNotificationsService";
import { UserService } from "../../core/userService/UserService";
import { CloudMessagingPermission } from "../models/CloudMessagingPermission";
import { CoreError, CoreErrorCause } from "../models/CoreError";
import { AnalyticsFeature } from "../analytics/analyticsFeature";
import { AnalyticsConstants } from "../../presentation/assets/constants/analytics.constants";
import { XCNotificationHelper } from "./models/Notification";
import {
  NotificationChannel,
  PartnerAllCardsUnlockedPress,
  PartnerDeckActivityPress,
  RemoteNotificationPress,
  hasNotificationSearchParams,
  parseNotificationLandingFromSearch,
  parseRemoteNotificationPressFromSearch,
} from "./models/RemoteNotificationPress";

export type { PartnerAllCardsUnlockedPress, PartnerDeckActivityPress };

const canRegisterDeviceToken = (
  permission: CloudMessagingPermission,
): boolean =>
  permission === CloudMessagingPermission.authorized ||
  permission === CloudMessagingPermission.provisionalAuthorized;

/** "not_allowed" = permission not granted, so no registration was attempted. */
type TokenRegistrationOutcome = "not_allowed" | "saved" | "failed";

const channelAnalyticsValue: Record<NotificationChannel, string> = {
  email: AnalyticsConstants.values.channels.email,
  push: AnalyticsConstants.values.channels.push,
};

/** Raw `notif` deep-link values → analytics values; anything else is `unknown`. */
const notificationTypeAnalyticsValue: Record<string, string> = {
  acceptedRequest: AnalyticsConstants.values.notificationTypes.acceptedRequest,
  characterMatch: AnalyticsConstants.values.notificationTypes.characterMatch,
  matches: AnalyticsConstants.values.notificationTypes.matches,
  partnerAllCardsUnlocked:
    AnalyticsConstants.values.notificationTypes.partnerAllCardsUnlocked,
  partnerDeckActivity:
    AnalyticsConstants.values.notificationTypes.partnerDeckActivity,
  request: AnalyticsConstants.values.notificationTypes.request,
  superlike: AnalyticsConstants.values.notificationTypes.superlike,
};

export class NotificationsFeature {
  private static tokenSyncInFlight: Promise<void> | null = null;
  private static tokenSyncedForUserId: string | null = null;
  private static pendingRemotePress: RemoteNotificationPress | null = null;

  private static getAuthUserId(): string | null {
    return AuthenticationService.getInstance().userId;
  }

  private static async getCurrentUserName(): Promise<string> {
    const userId = this.getAuthUserId();
    if (!userId) {
      return "";
    }
    const user = await UserService.getInstance().loadUser(userId);
    return user.fullName ?? "";
  }

  public static async sendPartnershipRequestNotification(
    receiverIdentifier: string,
  ): Promise<void> {
    const senderName = await this.getCurrentUserName();
    const notification = XCNotificationHelper.request(
      senderName,
      receiverIdentifier,
    );
    await PushNotificationsService.getInstance().saveNotification(notification);
  }

  public static async sendAcceptedRequestNotification(
    receiverIdentifier: string,
  ): Promise<void> {
    const senderName = await this.getCurrentUserName();
    const notification = XCNotificationHelper.acceptedRequest(
      senderName,
      receiverIdentifier,
    );
    await PushNotificationsService.getInstance().saveNotification(notification);
  }

  public static async sendMatchesNotification(
    receiverIdentifier: string,
    count: number,
  ): Promise<void> {
    const notification = XCNotificationHelper.matches(
      count,
      receiverIdentifier,
    );
    await PushNotificationsService.getInstance().saveNotification(notification);
  }

  public static async sendSuperlikeNotification(
    receiverIdentifier: string,
  ): Promise<void> {
    const notification = XCNotificationHelper.superlike(receiverIdentifier);
    await PushNotificationsService.getInstance().saveNotification(notification);
  }

  // ==================== Receiving (FCM web push) ====================

  public static isPushSupported(): Promise<boolean> {
    return PushNotificationsService.getInstance().isPushSupported();
  }

  public static isPushPermissionEnabled(
    permission: CloudMessagingPermission,
  ): boolean {
    return canRegisterDeviceToken(permission);
  }

  public static getPushPermissionStatus(): Promise<CloudMessagingPermission> {
    return PushNotificationsService.getInstance().getPermissionStatus();
  }

  /** Resolves `saved` once the token is stored for the user. */
  private static async registerDeviceTokenIfAllowed(
    userId: string,
  ): Promise<TokenRegistrationOutcome> {
    try {
      const service = PushNotificationsService.getInstance();
      const permission = await service.getPermissionStatus();
      if (!this.isPushPermissionEnabled(permission)) {
        return "not_allowed";
      }
      return (await service.registerDeviceToken(userId)) ? "saved" : "failed";
    } catch {
      // Token registration can temporarily fail during app startup.
      // We'll retry on the next start or permission re-check.
      return "failed";
    }
  }

  /**
   * `push_token_registered` distinguishes "permission granted" from "a token
   * actually landed" — the deliverability signal the permission events can't
   * carry. Fired only when a registration ran (not for `not_allowed`), and
   * only from the sign-in and permission-grant moments to bound volume.
   */
  private static trackTokenRegistration(
    outcome: TokenRegistrationOutcome,
  ): void {
    if (outcome === "not_allowed") {
      return;
    }
    AnalyticsFeature.trackEvent(AnalyticsConstants.events.pushTokenRegistered, {
      [AnalyticsConstants.params.result]:
        outcome === "saved"
          ? AnalyticsConstants.values.results.success
          : AnalyticsConstants.values.results.error,
    });
  }

  /**
   * Ensures the device token is registered for the signed-in user whenever
   * permission turns out to be granted — e.g. after the user flipped it in
   * the browser's site settings and came back. Coalesced, and a no-op once it
   * succeeded for the current user. Web has no token-refresh event; stale
   * tokens are pruned server-side on send failure.
   */
  public static syncDeviceTokenForCurrentUser(): Promise<void> {
    const userId = this.getAuthUserId();
    if (!userId || this.tokenSyncedForUserId === userId) {
      return Promise.resolve();
    }
    if (this.tokenSyncInFlight) {
      return this.tokenSyncInFlight;
    }
    this.tokenSyncInFlight = this.registerDeviceTokenIfAllowed(userId)
      .then((outcome) => {
        if (outcome === "saved") {
          this.tokenSyncedForUserId = userId;
        }
      })
      .finally(() => {
        this.tokenSyncInFlight = null;
      });
    return this.tokenSyncInFlight;
  }

  /** Registers the token (when permission is already granted) on sign-in. */
  public static async startForAuthenticatedUser(userId: string): Promise<void> {
    if (!(await this.isPushSupported())) {
      return;
    }
    const outcome = await this.registerDeviceTokenIfAllowed(userId);
    if (outcome === "saved") {
      this.tokenSyncedForUserId = userId;
    }
    this.trackTokenRegistration(outcome);
  }

  /**
   * Sign-out teardown: removes this browser's token from RTDB and invalidates
   * it, so the next account on a shared browser never receives this user's
   * pushes. Must run while the user is still authenticated (rules).
   */
  public static async stopForAuthenticatedUser(): Promise<void> {
    const userId = this.getAuthUserId();
    this.tokenSyncedForUserId = null;
    if (!userId || !(await this.isPushSupported())) {
      return;
    }
    try {
      await PushNotificationsService.getInstance().removeDeviceToken(userId);
    } catch {
      // Best-effort: the backend prunes dead tokens on the next send anyway.
    }
  }

  /**
   * Shows the browser prompt (call from a click handler only) and registers
   * the token when granted.
   */
  public static async requestPushPermissionsAndSyncToken(): Promise<CloudMessagingPermission> {
    const service = PushNotificationsService.getInstance();
    const permission = await service.requestPermissions();
    const userId = this.getAuthUserId();

    if (!userId || !this.isPushPermissionEnabled(permission)) {
      return permission;
    }

    // registerDeviceToken never rejects; a false return means every attempt
    // failed — leave the sync flag unset so the next check can retry.
    const didSave = await service.registerDeviceToken(userId);
    if (didSave) {
      this.tokenSyncedForUserId = userId;
    }
    this.trackTokenRegistration(didSave ? "saved" : "failed");
    return permission;
  }

  // ==================== Remote notification taps ====================

  /**
   * On web a notification tap opens `/app?notif=…&partnershipId=…` (the
   * push's `fcmOptions.link`). Captured once at startup into a pending press
   * the Partners surface consumes once it has its data, and the params are
   * stripped so a reload doesn't replay the deep link.
   */
  public static captureRemotePressFromUrl(): void {
    try {
      const landing = parseNotificationLandingFromSearch(
        window.location.search,
      );
      if (landing) {
        // The open-rate numerator: fires for every notification-link landing,
        // including types with no in-app destination and untyped legacy links.
        AnalyticsFeature.trackEvent(
          AnalyticsConstants.events.notificationLinkOpened,
          {
            [AnalyticsConstants.params.channel]:
              channelAnalyticsValue[landing.channel],
            [AnalyticsConstants.params.notificationType]:
              notificationTypeAnalyticsValue[landing.notifType ?? ""] ??
              AnalyticsConstants.values.notificationTypes.unknown,
            ...(landing.partnershipId
              ? {
                  [AnalyticsConstants.params.partnershipId]:
                    landing.partnershipId,
                }
              : {}),
          },
        );
      }
      const press = parseRemoteNotificationPressFromSearch(
        window.location.search,
      );
      if (press) {
        this.pendingRemotePress = press;
      }
      if (hasNotificationSearchParams(window.location.search)) {
        window.history.replaceState(
          window.history.state,
          "",
          window.location.pathname,
        );
      }
    } catch (error) {
      // replaceState can throw (SecurityError); with no error boundary a
      // throw out of the router effect would blank the whole app.
      new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Failed to capture notification press from URL",
        underlyingException: error,
      });
    }
  }

  public static peekPendingRemotePress(): RemoteNotificationPress | null {
    return this.pendingRemotePress;
  }

  public static consumeRemotePress(): void {
    this.pendingRemotePress = null;
  }
}
